import { useState } from 'react';
import { api } from '@/client/api';
import { useProcedure } from '@/client/useProcedure';
import { setModel, useModelState } from '@/store/store';
import { Section, SettingCard, SettingRow, SettingToggle } from './kit';
import { ChoiceRow } from './choice';

/** Settings → Display.
 *
 *  Theme is the one setting here that must apply the instant it is chosen rather than on the next load — a trader
 *  picking "light" and seeing nothing happen for a second will click it again. So it writes to the store first and
 *  persists behind that, which is also what makes it survive a reload and what the chart reads to re-palette itself.
 *
 *  The rest are stored preferences that change how figures are written. They are deliberately few: a display section
 *  with thirty switches is a display section nobody reads.
 */
const TIMEZONES = [
  'UTC',
  'Europe/London',
  'Europe/Zurich',
  'America/New_York',
  'America/Chicago',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'CHF', 'JPY', 'AUD'];

export function DisplaySection() {
  const S = useModelState();
  const { state, reload } = useProcedure(() => api.me.settings.get(), []);
  const [saving, setSaving] = useState(false);

  const save = async (patch: Parameters<typeof api.me.settings.update>[0]): Promise<void> => {
    setSaving(true);
    try {
      await api.me.settings.update(patch);
      reload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section state={state} reload={reload}>
      {(data) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <SettingCard
            title="Appearance"
            sub="Theme applies immediately and is remembered on this device. The chart follows it."
          >
            <ChoiceRow
              label="Theme"
              hint="System follows your operating system's own light or dark setting."
              value={S.theme === 'light' ? 'light' : 'dark'}
              options={[
                ['dark', 'Dark'],
                ['light', 'Light'],
              ]}
              disabled={saving}
              onPick={(value) => {
                const theme = value === 'light' ? ('light' as const) : ('dark' as const);
                // Store first: the whole app and the chart re-theme on this, and waiting for a round trip would
                // make the control feel broken.
                setModel({ theme });
                void save({ display: { theme } });
              }}
            />
            <ChoiceRow
              label="Density"
              hint="Compact tightens row heights across tables and lists."
              value={data.display.density}
              options={[
                ['comfortable', 'Comfortable'],
                ['compact', 'Compact'],
              ]}
              disabled={saving}
              onPick={(value) => void save({ display: { density: value as 'comfortable' | 'compact' } })}
            />
          </SettingCard>

          <SettingCard
            title="Figures"
            sub="How money and time are written. These change presentation only — never a stored value."
          >
            <ChoiceRow
              label="Number format"
              hint="Accounting writes losses in brackets, as a statement does."
              value={data.display.numberFormat}
              options={[
                ['plain', 'Plain'],
                ['accounting', 'Accounting'],
              ]}
              disabled={saving}
              onPick={(value) => void save({ display: { numberFormat: value as 'plain' | 'accounting' } })}
            />
            <SettingRow
              label="Time zone"
              hint="Session windows and trade times are shown in this zone. Stored times stay UTC."
            >
              <Picker
                value={data.display.timezone}
                options={TIMEZONES}
                disabled={saving}
                label="Time zone"
                onPick={(timezone) => void save({ display: { timezone } })}
              />
            </SettingRow>
            <SettingRow
              label="Base currency"
              hint="What P&L is expressed in. Account balances keep their own currency."
            >
              <Picker
                value={data.display.baseCurrency}
                options={CURRENCIES}
                disabled={saving}
                label="Base currency"
                onPick={(baseCurrency) => void save({ display: { baseCurrency } })}
              />
            </SettingRow>
          </SettingCard>

          <SettingCard
            title="This device"
            sub="Remembered here rather than on your account, because they describe this screen rather than you."
          >
            <SettingRow
              label="Favourites only on the desk"
              hint="Filters the order desk's instrument list to the ones you have starred."
            >
              <SettingToggle
                on={S.deskFavOnly}
                label="Show only favourite instruments on the desk"
                onClick={() => setModel((s) => ({ deskFavOnly: !s.deskFavOnly }))}
              />
            </SettingRow>
          </SettingCard>
        </div>
      )}
    </Section>
  );
}

/** A short list picked from a native select — the right control for eight timezones, where a segmented row would
 *  wrap and a custom menu would be a menu nobody asked for. */
function Picker({
  value,
  options,
  onPick,
  disabled,
  label,
}: {
  value: string;
  options: readonly string[];
  onPick: (value: string) => void;
  disabled: boolean;
  label: string;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      aria-label={label}
      onChange={(e) => onPick(e.target.value)}
      style={{
        fontSize: '11.5px',
        fontFamily: 'inherit',
        color: 'var(--text-mid)',
        background: 'transparent',
        border: '1px solid var(--l8)',
        borderRadius: '99px',
        padding: '5px 11px',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}
